import { useState, useEffect } from 'react';
import { userService } from '../../services/user.service';
import PermissionChecker from '../UI/PermissionChecker/PermissionChecker';
import styles from './Users.module.css';
import Input from '../UI/Input/Input';
import Select from '../UI/Select/Select';
import TableGrid from '../UI/Table/TableGrid/TableGrid';
import ColoredText from '../UI/Table/ColoredText/ColoredText';
import DateTime from '../UI/Table/DateTime/DateTime';
import UserCreateForm from './UserCreateFrom/UserCreateFrom';
import Button from '../UI/Button/Button';
import { useTranslation } from 'react-i18next';
import { IoIosRefresh } from 'react-icons/io';
import IconButton from '../UI/IconButton/IconButton';
import { useDispatch, useSelector } from 'react-redux';
import { selectUsersTableFilters } from '../../redux/slices/tableStatesSlice';
import { setUsersTableFilters } from '../../redux/slices/tableStatesSlice';
import UserActionsCell from '../UI/Table/UserActionsCell/UserActionsCell';
import { PERMISSIONS } from '../../shared/permission';
import { selectAuth } from '../../redux/slices/auth';

function Users() {
    const dispatch = useDispatch();
    const auth = useSelector(selectAuth);
    const usersTableFilters = useSelector(selectUsersTableFilters);
    const { t } = useTranslation('common');
    const [refreshKey, setRefreshKey] = useState(0);
    const PAGE_SIZE_OPTIONS = [10, 25, 50];
    const validPageSize = PAGE_SIZE_OPTIONS.includes(usersTableFilters.pageSize)
        ? usersTableFilters.pageSize
        : 10;

    const [search, setSearch] = useState('');
    const [tableFilters, setTableFilters] = useState({
        search: '',
        code: '',
        page: 0,
        pageSize: validPageSize,
    });
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: validPageSize,
    });
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    useEffect(() => {
        if (search.length === 0 || search.length >= 3) {
            setTableFilters(prev => ({ ...prev, search }));
        }
    }, [search]);

    const handleTableFiltersChange = (key, value) => {
        setTableFilters(prev => ({ ...prev, [key]: value }));
    };

    const roles = [
        { value: '', label: t('users.roles.all') },
        { value: 'admin', label: t('users.roles.admin') },
        { value: 'inventor', label: t('users.roles.inventor') },
        { value: 'expert', label: t('users.roles.expert') }
    ]

    const colorMap = {
        'active': 'green',
        'inactive': 'gray',
        'pending': 'yellow',
        'blocked': 'red',
    }

    const renderStatus = (params) => {
        return <ColoredText value={t(`users.statuses.${params.value}`)} colorMap={colorMap} colorCondition={params.value} />;
    };

    const renderDateTime = (params) => {
        return <DateTime date={params.value} />;
    };

    const renderActions = (params) => {
        return <UserActionsCell params={params} onSuccess={refreshTable} />;
    };

    const columns = [
        { field: 'id', headerName: '№', width: 40, flex: 0, sortable: false },
        { field: 'email', headerName: t('users.email'), width: 250, flex: 1, sortable: false },
        {
            field: 'status',
            headerName: t('users.status'),
            width: 150,
            flex: 0,
            sortable: false,
            renderCell: renderStatus,
        },
        { field: 'full_name', headerName: t('users.full_name'), width: 150, flex: 1, sortable: false },
        { field: 'code', headerName: t('users.role'), width: 150, flex: 0, sortable: false },
        {
            field: 'created_at',
            headerName: t('users.created_at'),
            width: 150,
            flex: 0,
            sortable: false,
            renderCell: renderDateTime,
        },
        {
            field: 'actions',
            headerName: t('users.actions'),
            width: 80,
            flex: 0,
            sortable: false,
            renderCell: renderActions,
        }
    ];

    if (auth?.user?.permissions?.some(permission => permission === PERMISSIONS.USERS.EDIT)) {
        columns.push(       {
            field: 'actions',
            headerName: t('users.actions'),
            width: 80,
            flex: 0,
            sortable: false,
            renderCell: renderActions,
        })
    }

    const refreshTable = () => {
        setPaginationModel((m) => ({ ...m, page: 0 }));
        setRefreshKey(prev => prev + 1);
    };

    const handleUserCreated = () => {
        refreshTable();
    };

    useEffect(() => {
        let alive = true;

        const load = async () => {
            setIsLoading(true);
            try {
                const response = await userService.getUsers({
                    queryParams: {
                        search: tableFilters.search,
                        code: tableFilters.code,
                        page: paginationModel.page + 1,
                        limit: paginationModel.pageSize,
                    }
                });
                if (alive) {
                    const usersTransformed = response.rows?.map(user => {
                        const firstName = user.first_name ? user.first_name : '';
                        const middleName = user.middle_name ? user.middle_name : '';
                        const lastName = user.last_name ? user.last_name : '';
                        const fullName = lastName + ' ' + firstName + ' ' + middleName;
                        return {
                            ...user,
                            full_name: fullName.trim() ? fullName : `${t(`users.roles.${user.code}`)} #${user.id}`,
                            code: t(`users.roles.${user.code}`),
                            created_at: user.created_at,
                        }
                    });
                    setUsers(usersTransformed ?? []);
                    setTotal(response.total ?? 0);
                }
            } catch (error) {
                console.log(error);
            } finally {
                if (alive) setIsLoading(false);
            }
        };
        load();
    }, [paginationModel.page, paginationModel.pageSize, tableFilters.search, tableFilters.code, t, refreshKey]);


    return (
        <div className={styles.container}>
            <div className={styles.tableWrapper}>
                <PermissionChecker permissions={[PERMISSIONS.USERS.VIEW]}>
                    <div className={styles.headerContainer}>
                        <IconButton onClick={refreshTable} variant="secondary">
                            <IoIosRefresh />
                        </IconButton>
                        <div className={styles.filtersContainer}>
                            <Input
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPaginationModel((m) => ({ ...m, page: 0 }));
                                }}
                                placeholder={t('users.search_placeholder')}
                                className={styles.input}
                            />
                            <Select
                                options={roles}
                                value={tableFilters.code}
                                onChange={(e) => {
                                    handleTableFiltersChange('code', e.target.value);
                                    setPaginationModel((m) => ({ ...m, page: 0 }));
                                }}
                                wrapperClassName={styles.selectFilterWrapper}
                            />
                        </div>
                        <PermissionChecker permissions={[PERMISSIONS.USERS.CREATE]}>
                            <Button
                                variant="primary"
                                onClick={() => setIsCreateModalOpen(true)}
                                className={styles.createButton}
                            >
                                {t('users.create')}
                            </Button>
                        </PermissionChecker>
                    </div>
                    <TableGrid
                        noRowsText={t('users.no_rows')}
                        columns={columns}
                        rows={users}
                        loading={isLoading}
                        rowCount={total}
                        paginationMode='server'
                        paginationModel={paginationModel}
                        pageSizeOptions={[10, 25, 50]}
                        sortingMode='server'
                        onPaginationModelChange={(model) => {
                            setPaginationModel(model);
                            dispatch(setUsersTableFilters({ 
                                ...usersTableFilters, 
                                page: model.page, 
                                pageSize: model.pageSize 
                            }));
                        }}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableColumnSort
                    />
                </PermissionChecker>
            </div>
            <PermissionChecker permissions={['users.create']}>
                <UserCreateForm
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSuccess={handleUserCreated}
                />
            </PermissionChecker>
        </div>
    )
}

export default Users;